// src/shared-components/board-comments/CommentList.jsx
import { useEffect, useState } from 'react';
import React from 'react';

import GalleryService from '@/services/gallery.service';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import './CommentList.css';

export default function CommentList({
  resourceType,
  resourceId,
  limit = 10,
  offset = 0,
  onReply,
  lastReplyParentId,
  scrollToComments = false,
}) {
  const commentsRef = React.useRef(null);
  const [comments, setComments] = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [error,     setError]    = useState(null);

  // map commentId → array of replies
  const [repliesMap, setRepliesMap] = useState({});
  // map commentId → boolean (open/closed)
  const [openReplies, setOpenReplies] = useState({});
  // map commentId → loading flag
  // eslint-disable-next-line no-unused-vars
  const [loadingReplies, setLoadingReplies] = useState({});

  // 1) Declare this first, so the effect can see it
  const handleToggleReplies = async (commentId) => {
    const isOpen = !!openReplies[commentId];
    // closing: just flip the flag
    if (isOpen) {
      setOpenReplies(m => ({ ...m, [commentId]: false }));
      return;
    }
    // opening: mark open
    setOpenReplies(m => ({ ...m, [commentId]: true }));
    // if not yet loaded, fetch
    if (!repliesMap[commentId]) {
      setLoadingReplies(m => ({ ...m, [commentId]: true }));
      try {
        const replies = await GalleryService.getCommentReplies(
          resourceType, 
          resourceId, 
          commentId
        );        setRepliesMap(m => ({ ...m, [commentId]: replies }));
      } catch (err) {
        console.error('Failed to load replies for', commentId, err);
      } finally {
        setLoadingReplies(m => ({ ...m, [commentId]: false }));
      }
    }
  };

  useEffect(() => {
    if (scrollToComments && !loading && commentsRef && commentsRef.current) {
      commentsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }, [loading, commentsRef, comments]);

  // 2) Fetch top-level comments
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    GalleryService.getComments(resourceType, resourceId, { limit, offset })
      .then(data => {
        if (isMounted) setComments(data);
      })
      .catch(err => {
        console.error('Error fetching comments', err);
        if (isMounted) setError('Failed to load comments');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [resourceType, resourceId, limit, offset]);

  // 3) Once loading finishes, auto-open that thread
  useEffect(() => {
    if (!loading && lastReplyParentId) {
      handleToggleReplies(lastReplyParentId);
    }
  }, [lastReplyParentId, loading]);

  if (loading) {
    return <Loader className="comment-list"></Loader>;
  }
  if (error) {
    return <div className="comment-list comment-list--error">{error}</div>;
  }

  return (
    <ul 
      className="comment-list"
      ref={commentsRef}
    >
      {comments.map(c => (
        <React.Fragment key={c._id}>
          {/* ——— Parent comment as Thought style ——— */}
          <li
            className={`
              artwork-view-thought__container
              element-non-selectable
              ${openReplies[c._id] ? "" : ""} /* openReplies doesn't change parent class */
            `}
          >
            {/* avatar */}
            <UserProfileImage
              className="artwork-view-thought__user-image"
              image={c.commenter.user_image?.image_id}
              typeOfProfile={c.commenter.user_type}
            />

            <div className="artwork-view-thought__text-section">
              {/* username */}
              <Text
                className="artwork-view-thought__small-text username dots-on-overflow font-size-medium"
                onClick={() => onReply && onReply(c)}>
                {c.commenter.user_username}
              </Text>

              {/* comment */}
              <Text className="artwork-view-thought__text">
                {Utils.parserUserMentions(c.comment_text)}
              </Text>

              {/* timestamp + reply link */}
              <div>
                <Text className="artwork-view-thought__small-text elapsed font-size-small">
                  {Utils.elapsedTime(new Date(c.createdAt).getTime())}
                </Text>
                {onReply && (
                  <Text
                    className="artwork-view-thought__small-text reply font-size-small"
                    onClick={() => onReply(c)}>
                    Reply
                  </Text>
                )}
              </div>

              {/* view/hide replies */}
              {c.reply_count > 0 && (
                <Text
                  className="artwork-view-thought__small-text view-more font-size-small"
                  onClick={() => handleToggleReplies(c._id)}>
                  {openReplies[c._id]
                    ? `Hide replies (${c.reply_count})`
                    : `View replies (${c.reply_count})`}
                </Text>
              )}

              {/* like */}
              <div className="artwork-view-thought__like-section">
                <LikeButton
                  itemId={c._id}
                  typeOfItem="COMMENT"
                  isLiked={c.is_liked}
                  onLikeAction={async newStatus => {
                    if (newStatus) {
                      await GalleryService.likeComment(c._id);
                    } else {
                      await GalleryService.unlikeComment(c._id);
                    }
                    // now update this list's state so the icon & count flip immediately:
                    setComments(cols =>
                      cols.map(x =>
                        x._id === c._id
                          ? { 
                            ...x,
                            is_liked: newStatus,
                            like_count: x.like_count + (newStatus ? 1 : -1)
                          }
                          : x
                      )
                    );
                  }}
                  className="artwork-view-thought__like"
                />
                <span className="artwork-view-thought__like-count">
                  {c.like_count > 0 && Utils.numberParserMillionThousand(c.like_count)}
                </span>
              </div>
            </div>
          </li>

          {/* ——— Replies, only shown when openReplies[c._id] is true ——— */}
          {openReplies[c._id] &&
            repliesMap[c._id]?.map(r => (
              <li
                key={r._id}
                className="artwork-view-thought__container element-non-selectable reply"
              >
                <UserProfileImage
                  className="artwork-view-thought__user-image"
                  image={r.commenter.user_image?.image_id}
                  typeOfProfile={r.commenter.user_type}
                />
                <div className="artwork-view-thought__text-section">
                  <Text
                    className="artwork-view-thought__small-text username dots-on-overflow font-size-medium">
                    {r.commenter.user_username}
                  </Text>
                  <Text className="artwork-view-thought__text font-size-large">
                    {Utils.parserUserMentions(r.comment_text)}
                  </Text>
                  <div>
                    <Text className="artwork-view-thought__small-text elapsed font-size-small">
                      {Utils.elapsedTime(new Date(r.createdAt).getTime())}
                    </Text>
                    {onReply && (
                      <Text
                        className="artwork-view-thought__small-text reply font-size-small"
                        onClick={() => onReply(r)}>
                        Reply
                      </Text>
                    )}
                  </div>
                  <div className="artwork-view-thought__like-section">
                    <LikeButton
                      itemId={r._id}
                      typeOfItem="COMMENT"
                      isLiked={r.is_liked}
                      className="artwork-view-thought__like"
                    />
                    <span className="artwork-view-thought__like-count">
                      {r.like_count > 0 && Utils.numberParserMillionThousand(r.like_count)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
        </React.Fragment>
      ))}
    </ul>
  );
}
